import { Flex, Text } from '@mantine/core';
import {
  useChatComposer,
  type ChatOutgoingPayload,
} from '@packages/chat/react';
import {
  ChatAttachButton,
  ChatComposerInput,
  ChatSendButton,
  ChatVoiceButton,
} from '@packages/chat/ui';
import { showNotification } from '@shared/utils';
import { useState } from 'react';
import { useRecordVoiceMessageModal } from './useRecordVoiceMessageModal';
import { useUserSintiaListenVoiceMessage } from './useUserSintiaListenVoiceMessage';

type UserSintiaChatMessageInputProps = {
  /** Resolves to whether the server accepted the message. */
  sendMessage: (payload: ChatOutgoingPayload) => Promise<boolean>;
  isSending: boolean;
};

export const UserSintiaChatMessageInput = ({
  sendMessage,
  isSending,
}: UserSintiaChatMessageInputProps) => {
  const { openVoiceMessage } = useUserSintiaListenVoiceMessage();
  const { openRecordVoiceMessage, closeRecordVoiceMessageModal } =
    useRecordVoiceMessageModal();
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const composer = useChatComposer({
    send: text => sendMessage({ type: 'text', data: text }),
    disabled: isSending,
  });

  const handleFileSelect = (file: File) => {
    setAudioFile(file);
    const audioUrl = URL.createObjectURL(file);

    openVoiceMessage({
      type: 'upload',
      audioUrl,
      fileName: file.name,
      onSend: () => {
        void sendMessage({ type: 'audio', data: file });
        setAudioFile(null);
        URL.revokeObjectURL(audioUrl);
      },
      onClose: () => {
        URL.revokeObjectURL(audioUrl);
        setAudioFile(null);
      },
    });
  };

  const handleInvalidFile = () => {
    showNotification({
      variant: 'error',
      message: 'Можно отправить только аудиофайл OGG или WebM с кодеком Opus',
    });
  };

  const openVoiceModal = () => {
    openRecordVoiceMessage({
      onSendFile: async file => {
        const sent = await sendMessage({ type: 'audio', data: file });
        if (sent) {
          closeRecordVoiceMessageModal();
        }
      },
    });
  };

  return (
    <Flex direction='column' gap={16} pt={16}>
      <Flex direction='column' gap={8}>
        <Text size='sm'>Сообщение сотруднику</Text>
        <Flex align='center'>
          <ChatComposerInput
            value={composer.text}
            onChange={event => composer.setText(event.currentTarget.value)}
            onKeyDown={composer.handleKeyDown}
            disabled={isSending}
            placeholder='Введите текст сообщения'
          />
          <ChatAttachButton
            title='Прикрепить аудио'
            disabled={isSending}
            value={audioFile}
            onSelect={handleFileSelect}
            onInvalidFile={handleInvalidFile}
          />
          <ChatVoiceButton
            title='Голосовое сообщение'
            disabled={isSending}
            onClick={openVoiceModal}
          />
        </Flex>
      </Flex>
      <Flex align='center' gap={12}>
        <ChatSendButton
          onClick={() => void composer.submit()}
          disabled={!composer.hasText || isSending}
          loading={isSending}
        >
          Отправить
        </ChatSendButton>
        <Text size='sm' opacity='60%'>
          Формат аудиофайла: OGG Кодировка OPUS *
        </Text>
      </Flex>
    </Flex>
  );
};
